import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Col, Button } from "react-bootstrap";
import { Product } from "../actions/types";
import ProductButtonsBuy from "./ProductButtonsBuy";
import ProductCarousel from "./ProductCarousel";
import { RootState } from "../reducers";
import { useSelector } from "react-redux";
import ProductTagsTypes from "./ProductTagsTypes";
import { PUBLIC_URL } from "../serviceWorker";
import { ProductTag } from "../actions/enums";
import useTenant from "../tenant/useTenant";
import { buildAssetPath } from "../tenant/tenantConfig";
import { formatPrice } from "./utilities";

interface Props {
  product: Product;
}

const Products = (props: Props) => {
  const [showButtons, setShowButtons] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const { product } = props;
  const tenant = useTenant();
  const pathImages = useSelector(
    (state: RootState) => state.shop.pathImages[product.id],
  );

  const displayId = selectedVariantId ?? product.id;
  const displayProduct = useSelector(
    (state: RootState) => state.shop.product[displayId],
  ) ?? product;
  const style = {
    boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2)",
    color: "#000",
    textDecoration: "none",
    cursor: "pointer",
  };
  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate("/product/" + product.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowButtons(!showButtons);
  };

  const types = useMemo(() => {
    return (displayProduct.types ?? []).filter((type: any) => {
      const typeName = type.name.toLowerCase();
      return (
        typeName !== ProductTag.WEB.toLowerCase() &&
        !typeName.includes(ProductTag.TALLE.toLowerCase()) &&
        !typeName.includes(ProductTag.COLOR.toLowerCase())
      );
    });
  }, [displayProduct.types]);

  const tagSale = useMemo(() => {
    return (displayProduct.types ?? []).filter(
      (type: any) => type.name === ProductTag.LIQUIDACION || type.name.match(/%/g),
    );
  }, [displayProduct.types]);

  const tagPremium = useMemo(() => {
    return (displayProduct.types ?? []).filter(
      (type: any) => type.name === ProductTag.PREMIUM,
    );
  }, [displayProduct.types]);

  const discountPercentage = Math.ceil(
    ((Number(displayProduct.price_regular) - Number(displayProduct.price)) /
      Number(displayProduct.price_regular)) *
      100,
  );

  return (
    <Col className="g-2">
      <Card border="light" style={style} onClick={handleNavigate}>
        {pathImages?.length > 1 ? (
          <ProductCarousel pathImages={pathImages} />
        ) : (
          <Card.Img src={product.path} />
        )}
        {tagSale[0]?.name && (
          <div
            className="rounded-1"
            style={{
              minWidth: 120,
              height: 35,
              top: 5,
              left: -5,
              backgroundColor: tenant.accentColor,
              position: "absolute",
              padding: 8,
            }}
          >
            <h6
              style={{
                textAlign: "center",
                fontSize: 14,
                color: "#000",
              }}
            >
              {tagSale[0].name}
            </h6>
          </div>
        )}
        {tagPremium[0]?.name && (
          <div
            className="rounded-1"
            style={{
              width: 118,
              height: 35,
              top: 5,
              left: -5,
              backgroundColor: tenant.accentColor,
              position: "absolute",
              paddingTop: 5,
            }}
          >
            <img
              style={{
                width: 33,
                marginLeft: 5,
                marginTop: -12,
                marginRight: 6,
              }}
              alt="PREMIUM"
              src={PUBLIC_URL + buildAssetPath(tenant.shopId, tenant.premiumBadgeFile || "premium.gif")}
            />
            <span
              style={{
                textAlign: "center",
                fontSize: 14,
                color: "#000",
                fontWeight: 500,
              }}
            >
              {tagPremium[0].name}
            </span>
          </div>
        )}
        <Card.Body className="d-flex flex-column h-100">
          <Card.Title className="fs-6 mb-1" style={{ minHeight: "2.5rem" }}>
            {displayProduct.name}
          </Card.Title>

          <div className="mb-2">
            {discountPercentage > 1 && (
              <Card.Text className="small text-decoration-line-through mb-0 fw-lighter text-body-secondary">
                ${formatPrice(displayProduct.price_regular)}
              </Card.Text>
            )}
            <Card.Title className="fs-5 d-flex flex-row align-items-center mb-0">
              <span className="fw-bold">${formatPrice(displayProduct.price)}</span>
              {discountPercentage > 1 && (
                <span
                  className="small ms-2 fw-semibold"
                  style={{
                    fontSize: "13px",
                    color: "rgb(0, 166, 80)",
                    backgroundColor: "#e8f5e9",
                    padding: "2px 6px",
                    borderRadius: "4px",
                  }}
                >
                  {discountPercentage}% OFF
                </span>
              )}
            </Card.Title>
          </div>

          <Card.Text className="small text-muted flex-grow-1 mb-3">
            {displayProduct.description}{" "}
            <ProductTagsTypes excludeTypes={[ProductTag.WEB]} types={types} />
          </Card.Text>

          <div className="mt-auto" onClick={(e) => e.stopPropagation()}>
            <Button
              variant={showButtons ? "dark" : "outline-dark"}
              size="sm"
              onClick={handleClick}
              className="w-100 rounded-pill fw-semibold"
              style={{ letterSpacing: "0.5px" }}
            >
              {showButtons ? "Cerrar opciones" : "Ver opciones"}
            </Button>
          </div>

          {showButtons && (
            <div
              className="mt-3 pt-3 border-top"
              onClick={(e) => e.stopPropagation()}
            >
              <ProductButtonsBuy
                productId={product.id}
                onResolvedProductChange={setSelectedVariantId}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default Products;
